import React, { useEffect, useState } from "react";

/**
 * useLightbox
 * Small hook that manages lightbox open/close state + the currently shown image.
 * Pairs with <ImageLightbox />. Extracted from the pattern used in VeryconDc.jsx
 * so it can be reused on any page/section (banners, product images, galleries...).
 *
 * Usage:
 *   const lightbox = useLightbox();
 *   <img onClick={() => lightbox.open(imageSrc)} className="cursor-zoom-in" />
 *   <ImageLightbox {...lightbox} />
 *   // lightbox = { isOpen, image, open, onClose } — spread directly, no renaming needed
 */
export function useLightbox() {
  const [isOpen, setIsOpen] = useState(false);
  const [image, setImage] = useState(null);

  const open = (src) => {
    setImage(src);
    setIsOpen(true);
  };

  const close = () => setIsOpen(false);

  // Returned as `onClose` (not `close`) so `{...lightbox}` spreads directly
  // into <ImageLightbox />'s `onClose` prop without renaming at the call site.
  return { isOpen, image, open, onClose: close };
}

/**
 * ImageLightbox
 * Fullscreen image popup: click-backdrop-to-close, Escape-to-close, close button.
 * Same visual treatment as the lightbox in VeryconDc.jsx (bg-black/80 backdrop,
 * rounded-2xl white frame, object-contain image).
 *
 * Props:
 * - isOpen: boolean
 * - image: string (image src) — lightbox renders nothing if falsy
 * - onClose: () => void
 * - alt: string (optional, defaults to "Enlarged view")
 */
export default function ImageLightbox({ isOpen, image, onClose, alt = "Enlarged view" }) {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !image) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-w-[90vw] max-h-[85vh] lg:max-w-[70vw] lg:max-h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden p-1"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 bg-black/60 hover:bg-black/80 text-white rounded-full w-9 h-9 flex items-center justify-center text-lg font-bold transition-colors duration-150"
          aria-label="Close"
        >
          ✕
        </button>
        <img
          src={image}
          alt={alt}
          className="w-full h-full max-h-[85vh] object-contain rounded-xl"
        />
      </div>
    </div>
  );
}